import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useCallRoutingStore } from '@/stores/call-routing-store'
import { Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { AddTargetDialog } from './add-target-dialog'
import { DeleteTargetDialog } from './delete-target-dialog'
import { GoodbyeCard } from './goodbye-card'
import { GreetingCard } from './greeting-card'
import { RoutingTargetCard } from './routing-target-card'

export function RoutingList() {
    // Get store actions
    const initCallRoutingConfiguration = useCallRoutingStore((state) => state.initCallRoutingConfiguration)
    const updateDefaultGreetingMessage = useCallRoutingStore((state) => state.updateDefaultGreetingMessage)
    const updateDefaultGoodbyeMessage = useCallRoutingStore((state) => state.updateDefaultGoodbyeMessage)
    const addNewDefaultCallRoutingTarget = useCallRoutingStore((state) => state.addNewDefaultCallRoutingTarget)
    const updateDefaultCallRoutingTarget = useCallRoutingStore((state) => state.updateDefaultCallRoutingTarget)
    const deleteDefaultCallRoutingTarget = useCallRoutingStore((state) => state.deleteDefaultCallRoutingTarget)

    // Subscribe to the configuration object - this triggers re-renders when it changes
    const callRoutingConfig = useCallRoutingStore((state) => state.callRoutingConfigurationObject)

    const [loading, setLoading] = useState(false)
    const [showAddDialog, setShowAddDialog] = useState(false)
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)
    const [currentTargetIndex, setCurrentTargetIndex] = useState<number>(-1)
    const [currentTargetNumber, setCurrentTargetNumber] = useState<string>('')
    const [currentTargetPhone, setCurrentTargetPhone] = useState<string>('')

    // Derive data from the subscribed state (reactive)
    const defaultElement = callRoutingConfig.selectionElements?.find(el => el.defaultElement) || null

    const greetingMessage = {
        message: defaultElement?.utteranceToSpeak || '',
        language: defaultElement?.utteranceLanguage || 'en'
    }

    const goodbyeMessage = {
        message: defaultElement?.goodbyeUtteranceToSpeak || defaultElement?.utteranceAtEnd || '',
        language: defaultElement?.goodbyeUtteranceLanguage || 'en'
    }

    const routingTargets = defaultElement?.possibleSelections || {}

    useEffect(() => {
        loadData()
    }, [])

    const loadData = async () => {
        setLoading(true)
        try {
            await initCallRoutingConfiguration()
        } catch (error) {
            console.error('Error loading configuration:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleUpdateGreeting = async (data: { message: string; language: string }) => {
        setLoading(true)
        try {
            await updateDefaultGreetingMessage(data)
            await loadData()
        } finally {
            setLoading(false)
        }
    }

    const handleUpdateGoodbye = async (data: { message: string; language: string }) => {
        setLoading(true)
        try {
            await updateDefaultGoodbyeMessage(data)
            await loadData()
        } finally {
            setLoading(false)
        }
    }

    const handleAddTarget = async (target: any) => {
        setLoading(true)
        try {
            await addNewDefaultCallRoutingTarget(target)
            await loadData()
            setShowAddDialog(false)
        } finally {
            setLoading(false)
        }
    }

    const handleUpdateTarget = async (target: any) => {
        setLoading(true)
        try {
            await updateDefaultCallRoutingTarget(target)
            await loadData()
        } finally {
            setLoading(false)
        }
    }

    const handleOpenDeleteDialog = (index: number, targetNumber: string, phoneNumber: string) => {
        setCurrentTargetIndex(index)
        setCurrentTargetNumber(targetNumber)
        setCurrentTargetPhone(phoneNumber)
        setShowDeleteDialog(true)
    }

    const handleDeleteTarget = async () => {
        if (currentTargetIndex === -1) return

        setLoading(true)
        try {
            await deleteDefaultCallRoutingTarget(currentTargetIndex)
            await loadData()
            setShowDeleteDialog(false)
        } finally {
            setLoading(false)
        }
    }

    if (loading && !routingTargets) {
        return (
            <div className="flex items-center justify-center p-12">
                <div className="text-gray-500">Lädt...</div>
            </div>
        )
    }

    const routingTargetList = routingTargets ? Object.entries(routingTargets) : []

    return (
        <div className="space-y-4">
            {/* Begrüßung */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-sm font-medium text-gray-600">Begrüßungsnachricht</CardTitle>
                </CardHeader>
                <CardContent>
                    <GreetingCard
                        title="Begrüßungsnachricht"
                        message={greetingMessage.message}
                        language={greetingMessage.language}
                        onUpdate={handleUpdateGreeting}
                    />
                </CardContent>
            </Card>

            {/* Weiterleitungsziele */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-sm font-medium text-gray-600">Weiterleitungsziele</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    {routingTargetList.length === 0 ? (
                        <div className="text-center py-8 text-gray-500 text-sm">
                            Keine Weiterleitungsziele konfiguriert
                        </div>
                    ) : (
                        routingTargetList.map(([targetNumber, targetObject]: [string, any], index) => (
                            <RoutingTargetCard
                                key={targetNumber}
                                index={index}
                                callRoutingTargetNumber={targetNumber}
                                routingTargetObject={targetObject}
                                onUpdate={handleUpdateTarget}
                                onDelete={() => handleOpenDeleteDialog(index, targetNumber, targetObject.phoneNumber)}
                            />
                        ))
                    )}

                    {/* Add Button */}
                    <button
                        onClick={() => setShowAddDialog(true)}
                        className="w-full border-2 border-dashed border-gray-200 rounded-lg p-4 hover:border-brand-primary hover:bg-brand-primary/5 transition-colors flex items-center justify-center gap-2 text-brand-primary"
                    >
                        <div className="bg-white p-2 rounded-full border border-gray-100">
                            <Plus className="h-5 w-5" />
                        </div>
                    </button>
                </CardContent>
            </Card>

            {/* Abschiedsnachricht */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-sm font-medium text-gray-600">Abschiedsnachricht</CardTitle>
                </CardHeader>
                <CardContent>
                    <GoodbyeCard
                        title="Abschiedsnachricht"
                        message={goodbyeMessage.message}
                        language={goodbyeMessage.language}
                        onUpdate={handleUpdateGoodbye}
                    />
                </CardContent>
            </Card>

            {/* Dialogs */}
            <AddTargetDialog
                isOpen={showAddDialog}
                onClose={() => setShowAddDialog(false)}
                onAdd={handleAddTarget}
            />

            <DeleteTargetDialog
                isOpen={showDeleteDialog}
                onClose={() => setShowDeleteDialog(false)}
                onDelete={handleDeleteTarget}
                targetNumber={currentTargetNumber}
                phoneNumber={currentTargetPhone}
            />
        </div>
    )
}
